const portRegistry = new Map<string, SerialPort>();
let activePort: SerialPort | null = null;
let activePortId: string | null = null;
let readAbortController: AbortController | null = null;
let readLoopPromise: Promise<void> | null = null;
let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
let onUnexpectedDisconnect: (() => void) | null = null;

export function isWebSerialSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.serial !== 'undefined';
}

function formatHex(value: number): string {
  return value.toString(16).padStart(4, '0');
}

export function formatWebSerialLabel(port: SerialPort): string {
  const info = port.getInfo();
  if (info.usbVendorId != null && info.usbProductId != null) {
    return `USB ${formatHex(info.usbVendorId)}:${formatHex(info.usbProductId)}`;
  }
  if (info.bluetoothServiceClassId) {
    return `Bluetooth ${info.bluetoothServiceClassId}`;
  }
  return 'Serial device';
}

export function getWebSerialPortId(port: SerialPort, index: number): string {
  const info = port.getInfo();
  const vid = info.usbVendorId != null ? formatHex(info.usbVendorId) : '0000';
  const pid = info.usbProductId != null ? formatHex(info.usbProductId) : '0000';
  return `webserial-${vid}-${pid}-${index}`;
}

export function registerWebSerialPort(port: SerialPort, index: number): string {
  const id = getWebSerialPortId(port, index);
  portRegistry.set(id, port);
  return id;
}

export function syncWebSerialRegistry(ports: SerialPort[]): void {
  portRegistry.clear();
  ports.forEach((port, index) => {
    registerWebSerialPort(port, index);
  });
}

export function getRegisteredWebSerialPort(portId: string): SerialPort | undefined {
  return portRegistry.get(portId);
}

export function getActivePortLabel(): string | null {
  if (!activePort || !activePortId) {
    return null;
  }
  return formatWebSerialLabel(activePort);
}

async function cancelActiveReader(): Promise<void> {
  const reader = activeReader;
  if (!reader) {
    return;
  }

  activeReader = null;
  try {
    await reader.cancel();
  } catch {
    // Reader may already be cancelled or the port may be closing.
  }

  try {
    reader.releaseLock();
  } catch {
    // Lock may already be released.
  }
}

async function stopReadLoop(): Promise<void> {
  readAbortController?.abort();
  await cancelActiveReader();

  if (readLoopPromise) {
    try {
      await readLoopPromise;
    } catch {
      // Loop exits with abort/errors during shutdown.
    }
    readLoopPromise = null;
  }

  readAbortController = null;
}

function startReadLoop(port: SerialPort, onReceive: (text: string) => void): void {
  readAbortController = new AbortController();
  const signal = readAbortController.signal;

  readLoopPromise = (async () => {
    while (!signal.aborted && port.readable) {
      activeReader = port.readable.getReader();
      try {
        while (!signal.aborted) {
          const { value, done } = await activeReader.read();
          if (done || signal.aborted) {
            if (done && !signal.aborted && port === activePort) {
              onUnexpectedDisconnect?.();
            }
            break;
          }
          if (value) {
            onReceive(new TextDecoder().decode(value));
          }
        }
      } catch {
        if (!signal.aborted && port === activePort) {
          onUnexpectedDisconnect?.();
        }
      } finally {
        if (activeReader) {
          try {
            activeReader.releaseLock();
          } catch {
            // Lock may already be released during shutdown.
          }
          activeReader = null;
        }
      }
    }
  })();
}

function handlePortDisconnect(event: Event): void {
  const port = (event as SerialDisconnectEvent).port;
  if (port !== activePort) {
    return;
  }

  onUnexpectedDisconnect?.();
}

export function setUnexpectedDisconnectHandler(handler: (() => void) | null): void {
  onUnexpectedDisconnect = handler;
}

async function refreshAuthorizedPorts(): Promise<SerialPort[]> {
  if (!navigator.serial) {
    return [];
  }

  const ports = await navigator.serial.getPorts();
  syncWebSerialRegistry(ports);
  return ports;
}

function resolvePortFromSelection(portId: string | null, authorizedPorts: SerialPort[]): {
  port: SerialPort | undefined;
  resolvedPortId: string;
} {
  if (portId) {
    const registered = portRegistry.get(portId);
    if (registered) {
      return { port: registered, resolvedPortId: portId };
    }
  }

  if (authorizedPorts.length === 1) {
    const port = authorizedPorts[0]!;
    return {
      port,
      resolvedPortId: registerWebSerialPort(port, 0),
    };
  }

  return { port: undefined, resolvedPortId: portId ?? '' };
}

async function ensurePortClosed(port: SerialPort): Promise<void> {
  if (activePort === port) {
    await closeWebSerialPort();
    return;
  }

  if (!port.readable && !port.writable) {
    return;
  }

  try {
    await port.readable?.cancel();
  } catch {
    // Ignore if the stream is already closed.
  }

  try {
    await port.close();
  } catch {
    // Port may already be closed.
  }
}

export async function authorizeWebSerialDevice(): Promise<{ id: string; label: string }> {
  if (!isWebSerialSupported() || !navigator.serial) {
    throw new Error('Web Serial is not supported in this browser. Use Chrome or Edge.');
  }

  const port = await navigator.serial.requestPort();
  const ports = await refreshAuthorizedPorts();
  const index = ports.indexOf(port);
  const id = registerWebSerialPort(port, index >= 0 ? index : ports.length - 1);

  return {
    id,
    label: formatWebSerialLabel(port),
  };
}

export async function openWebSerialPort(
  portId: string | null,
  baudRate: number,
  onReceive: (text: string) => void,
): Promise<{ portId: string; label: string }> {
  if (!isWebSerialSupported() || !navigator.serial) {
    throw new Error('Web Serial is not supported in this browser. Use Chrome or Edge.');
  }

  await closeWebSerialPort();

  const authorizedPorts = await refreshAuthorizedPorts();
  let { port, resolvedPortId } = resolvePortFromSelection(portId, authorizedPorts);

  if (!port) {
    port = await navigator.serial.requestPort();
    const ports = await refreshAuthorizedPorts();
    const index = ports.indexOf(port);
    resolvedPortId = registerWebSerialPort(port, index >= 0 ? index : ports.length - 1);
  }

  await ensurePortClosed(port);
  await port.open({ baudRate });

  activePort = port;
  activePortId = resolvedPortId;
  port.addEventListener('disconnect', handlePortDisconnect);
  startReadLoop(port, onReceive);

  return {
    portId: resolvedPortId,
    label: formatWebSerialLabel(port),
  };
}

export async function closeWebSerialPort(): Promise<void> {
  const port = activePort;

  await stopReadLoop();

  if (!port) {
    activePortId = null;
    return;
  }

  port.removeEventListener('disconnect', handlePortDisconnect);

  try {
    await port.readable?.cancel();
  } catch {
    // Ignore if the stream is already closed.
  }

  try {
    if (port.readable || port.writable) {
      await port.close();
    }
  } catch {
    // Device may already be unplugged.
  }

  activePort = null;
  activePortId = null;
}

/** Close every authorized Web Serial port so arduino-cli can take the COM port on Windows. */
export async function releaseAllWebSerialPortsForUpload(): Promise<void> {
  if (!isWebSerialSupported() || !navigator.serial) {
    return;
  }

  await closeWebSerialPort();

  const ports = await refreshAuthorizedPorts();
  await Promise.all(
    ports.map(async (port) => {
      try {
        if (port.readable || port.writable) {
          await port.readable?.cancel().catch(() => undefined);
          await port.close();
        }
      } catch {
        // Port may already be closed or unplugged.
      }
    }),
  );
}

export async function writeWebSerial(data: string): Promise<void> {
  if (!activePort?.writable) {
    throw new Error('Serial port is not open.');
  }

  const writer = activePort.writable.getWriter();
  try {
    await writer.write(new TextEncoder().encode(data));
  } finally {
    writer.releaseLock();
  }
}

export async function listWebSerialPorts(): Promise<Array<{ id: string; label: string }>> {
  if (!isWebSerialSupported() || !navigator.serial) {
    return [];
  }

  const ports = await refreshAuthorizedPorts();

  return ports.map((port, index) => ({
    id: getWebSerialPortId(port, index),
    label: formatWebSerialLabel(port),
  }));
}
